import { Injectable } from '@angular/core';
import { catchError, debounceTime, EMPTY, finalize, map, Observable, of, shareReplay, Subject } from 'rxjs';
import {
	IDevice,
	IMediaPlan,
	IPanel,
	IPanelConfig,
	IPanelLog,
	IPanelStatisticTotal,
} from '../models/interfaces/device.interface';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { MediaPlanResponse } from '~models/types/media-plan-response.type';

@Injectable({
	providedIn: 'root',
})
export class DeviceService {
	public deviceInfoLoading: Subject<boolean> = new Subject<boolean>();

	public deviceLogLoading: Subject<boolean> = new Subject<boolean>();

	private deviceMediaplanLoading: Subject<boolean> = new Subject<boolean>();

	private deviceRawMediaplanLoading: Subject<boolean> = new Subject<boolean>();

	public deviceMediaplanLoading$ = this.deviceMediaplanLoading
		.asObservable()
		.pipe(shareReplay({ bufferSize: 1, refCount: true }));

	public deviceRawMediaplanLoading$ = this.deviceRawMediaplanLoading
		.asObservable()
		.pipe(shareReplay({ bufferSize: 1, refCount: true }));

	public deviceConfigLoading: Subject<boolean> = new Subject<boolean>();

	private apiUrl = environment.apiUrl;

	constructor(private httpClient: HttpClient) {}

	public getDeviceInfo(uuid: string): Observable<IDevice> {
		this.deviceInfoLoading.next(true);
		return this.httpClient.get<IDevice>(`${this.apiUrl}/api/devices/${uuid}`).pipe(
			debounceTime(1000),
			catchError((error) => {
				this.deviceInfoLoading.next(false);
				console.error(error);
				return EMPTY;
			})
		);
	}

	public getDeviceLogs(uuid: string): Observable<IPanelLog[]> {
		this.deviceLogLoading.next(true);
		return this.httpClient.get<IPanelLog[]>(`${this.apiUrl}/api/logs/device/${uuid}`).pipe(
			debounceTime(1000),
			catchError((error) => {
				this.deviceLogLoading.next(false);
				console.error(error);
				return EMPTY;
			})
		);
	}

	public getDeviceMediaplan(uuid: string): Observable<IMediaPlan[]> {
		this.deviceMediaplanLoading.next(true);
		return this.httpClient.get<MediaPlanResponse>(`${this.apiUrl}/api/devices/${uuid}/mediaplan`).pipe(
			map((data: MediaPlanResponse) => this.transformRawMediaPlan(data)),
			catchError((error) => {
				console.error(error);
				return EMPTY;
			}),
			finalize(() => this.deviceMediaplanLoading.next(false))
		);
	}

	public getDeviceRawMediaplan(uuid: string): Observable<MediaPlanResponse> {
		this.deviceRawMediaplanLoading.next(true);
		return this.httpClient.get<MediaPlanResponse>(`${this.apiUrl}/api/devices/${uuid}/mediaplan`).pipe(
			catchError((error) => {
				console.error(error);
				return EMPTY;
			}),
			finalize(() => this.deviceRawMediaplanLoading.next(false))
		);
	}

	public updateDeviceMediaplan(uuid: string, body: IMediaPlan[]): Observable<boolean | null> {
		this.deviceMediaplanLoading.next(true);
		const convertedBody = this.transformMediaPlan(body);

		return this.httpClient.put<MediaPlanResponse>(`${this.apiUrl}/api/devices/${uuid}/mediaplan`, convertedBody).pipe(
			map(() => true),
			catchError((error) => {
				console.error(error);
				return of(null);
			}),
			finalize(() => this.deviceMediaplanLoading.next(false))
		);
	}

	public updateDeviceRawMediaplan(uuid: string, body: MediaPlanResponse): Observable<boolean | null> {
		this.deviceMediaplanLoading.next(true);

		return this.httpClient.put<MediaPlanResponse>(`${this.apiUrl}/api/devices/${uuid}/mediaplan`, body).pipe(
			map(() => true),
			catchError((error) => {
				console.error(error);
				return of(null);
			}),
			finalize(() => this.deviceMediaplanLoading.next(false))
		);
	}

	public updateDeviceMediaplanFromFile(uuid: string): Observable<boolean | null> {
		this.deviceMediaplanLoading.next(true);

		return this.httpClient.put<unknown>(`${this.apiUrl}/api/devices/${uuid}/mediaplan/reload`, {}).pipe(
			map(() => true),
			catchError((error) => {
				console.error(error);
				return of(null);
			}),
			finalize(() => this.deviceMediaplanLoading.next(false))
		);
	}

	public checkMediaplan(uuid: string): Observable<string> {
		return this.httpClient.get<string>(`${this.apiUrl}/api/devices/${uuid}/mediaplan/check`).pipe(
			catchError((error) => {
				console.error(error);
				return of('Error');
			})
		);
	}

	public getDeviceConfig(uuid: string): Observable<IPanelConfig> {
		this.deviceConfigLoading.next(true);
		return this.httpClient.get<IPanelConfig>(`${this.apiUrl}/api/devices/${uuid}/config`).pipe(
			debounceTime(1000),
			catchError((error) => {
				this.deviceConfigLoading.next(false);
				console.error(error);
				return EMPTY;
			})
		);
	}

	public getDeviceStatistic(uuid: string, start: string, end: string): Observable<IPanelStatisticTotal> {
		return this.httpClient
			.get<IPanelStatisticTotal>(`${this.apiUrl}/api/devices/${uuid}/statistics?from=${start}&to=${end}&detailed=true`)
			.pipe(debounceTime(1000));
	}

	public transformRawMediaPlan(data: MediaPlanResponse) {
		return (
			data?.map((item) => {
				const res = {
					adv: item.panels.adv,
					nav: item.panels.nav,
					duration: item.duration,
				};
				if (item.panels.adv2) {
					Object.assign(res, { adv2: item.panels.adv2 });
				}
				if (item.panels.nav2) {
					Object.assign(res, { nav2: item.panels.nav2 });
				}
				return res;
			}) ?? []
		);
	}

	public updateDeviceDescription(uuid: string, description: string): Observable<boolean | null> {
		this.deviceLogLoading.next(true);

		return this.httpClient.patch<unknown>(`${this.apiUrl}/api/devices/${uuid}/update`, { description }).pipe(
			map(() => true),
			catchError((error) => {
				console.error(error);
				return of(null);
			}),
			finalize(() => this.deviceLogLoading.next(false))
		);
	}

	/**
	 * Преобразует массив IMediaPlan в массив объектов с панелями
	 * @param body - исходный массив IMediaPlan
	 * @returns массив преобразованных объектов
	 */
	private transformMediaPlan(body: IMediaPlan[]): MediaPlanResponse {
		if (!body || !Array.isArray(body)) {
			return [];
		}

		const res = body.map((item: IMediaPlan) => {
			// Создаем объект panels
			const panels: IPanel = {
				adv: item.adv,
			};

			// Добавляем nav только если он есть
			if (item.nav != null && item.nav !== '') {
				panels.nav = item.nav;
			}

			// Добавляем adv2 только если он есть
			if (item.adv2 != null && item.adv2 !== '') {
				panels.adv2 = item.adv2;
			}

			// Добавляем nav2 только если он есть
			if (item.nav2 != null && item.nav2 !== '') {
				panels.nav2 = item.nav2;
			}

			return {
				panels,
				duration: item.duration,
			};
		});
		return res;
	}
}
